import { inject, Pipe, PipeTransform } from '@angular/core';

/**
 * libs
 */
import { TranslateService } from '@ngx-translate/core';

/**
 * Pipe that recursively translates labels in an array of objects, including nested
 * items. Uses dot notation to locate the nested array and a key to translate.
 *
 * @example
 * ```html
 * <app-menu [items]="Items | translateList:'subItems':'label'">
 * ```
 */
@Pipe({
  name: 'translateList',
})
export class TranslateListPipe implements PipeTransform {

  /** Translate service for i18n. */
  private readonly _translate = inject(TranslateService);

  /**
   * Translates labels in an array recursively based on notation and translateKey.
   *
   * @param value - Array of objects to translate (e.g. menu items).
   * @param args - Positional args:
   *   - `notation` - Dot path to the nested array (e.g. `'subItems'` or `'item.children'`).
   *   - `translateKey` - Property name holding the translation key (e.g. `'label'`).
   * @returns New array with translated labels.
   */
  public transform(value: unknown[], translateKey: string, config?: { notation?: string }): any[] {
    
    // if the value is not an array, return an empty array
    if (!Array.isArray(value)) {
      console.error('Value is not an array');
      return [];
    }

    // get the notation and translate key from the args
    const { notation } = config ?? {};
    
    return this._chainArray(value ?? [], translateKey, notation);
  }

  /**
   * Recursively walks the array, translates the translateKey at each level, and
   * recurses into nested arrays found at the notation path.
   *
   * @param value - Array to process.
   * @param notation - Dot path to nested array (e.g. `'subItems'`).
   * @param translateKey - Property holding the i18n key (e.g. `'label'`).
   */
  private _chainArray(value: any[], translateKey: string, notation?: string): any[] {
    
    // split notation into an array
    const _notation = notation?.split('.') ?? [];
    
    return value.map(item => {
      
      // reduce the item to the child
      const _child = _notation.reduce((obj, key) => obj?.[key], item);

      // if the child is not null, return the item with the translated key and the sub items
      if (Array.isArray(_child) && notation && notation.length > 0) {
        return { 
          ...item,
          [translateKey]: this._translate.instant(item[translateKey]),
          [notation]: this._chainArray(_child, translateKey, notation)
        };
      }
      else {
        return {...item, [translateKey]: this._translate.instant(item[translateKey])};
      }

    });
  }

}
